import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  ActionModalAction,
  ActionModalVariant,
} from '@/components/molecules'
import { useSubmitPetClaim } from '@/shared/hooks/queries/use-submit-pet-claim'
import { ApiProblemError } from '@/shared/helpers/api-problem'
import {
  CLAIM_PROBLEM_TYPE,
  MISSING_FIELDS_SEPARATOR,
} from '@/shared/constants/claims'
import { PET_CLAIM_STATUS } from '@/shared/types/global.types'
import type { ClaimMissingField, PetClaim } from '@/shared/types/global.types'

type ModalStep = 'closed' | 'confirm' | 'incomplete' | 'submitted'

interface UseSubmitClaimModalProps {
  /** Closing without submitting; the claim stays a draft */
  onCancel: (claimId: string) => void
  /** Leaving after a submit, or after a draft that can't be submitted yet */
  onFinish: (claimId: string) => void
}

// After a claim is saved: offers to submit it when it's complete, or lists
// what's missing when it isn't. Shared by create, edit and the claim detail
export function useSubmitClaimModal({
  onCancel,
  onFinish,
}: UseSubmitClaimModalProps) {
  const { t } = useTranslation()
  const submitMutation = useSubmitPetClaim()
  const [step, setStep] = useState<ModalStep>('closed')
  const [claimId, setClaimId] = useState('')
  const [missingFields, setMissingFields] = useState<ClaimMissingField[]>([])
  const [errorKey, setErrorKey] = useState<string | undefined>(undefined)

  function open(claim: PetClaim) {
    setClaimId(claim.id)
    setErrorKey(undefined)
    setMissingFields(claim.missing_fields)
    setStep(
      claim.status === PET_CLAIM_STATUS.FILEABLE ? 'confirm' : 'incomplete',
    )
  }

  function handleSubmitError(error: Error) {
    if (!(error instanceof ApiProblemError)) {
      setErrorKey('claims.submit.errors.generic')
      return
    }
    switch (error.problem.type) {
      case CLAIM_PROBLEM_TYPE.CLAIM_ALREADY_FILED:
        // Already with the core: nothing left to do
        setStep('submitted')
        return
      case CLAIM_PROBLEM_TYPE.CLAIM_NOT_FILEABLE:
        setMissingFields(error.problem.missing_fields ?? [])
        setStep('incomplete')
        return
      case CLAIM_PROBLEM_TYPE.CLAIM_FILED_NOT_RECORDED:
        // The core has it; resubmitting repairs the record without filing twice
        setErrorKey('claims.submit.errors.notRecorded')
        return
      case CLAIM_PROBLEM_TYPE.INSURANCE_CORE_UNAVAILABLE:
        setErrorKey('claims.submit.errors.coreUnavailable')
        return
      default:
        setErrorKey('claims.submit.errors.generic')
    }
  }

  function handleSubmit() {
    setErrorKey(undefined)
    submitMutation.mutate(claimId, {
      onSuccess: () => setStep('submitted'),
      onError: handleSubmitError,
    })
  }

  function handleCancel() {
    setStep('closed')
    onCancel(claimId)
  }

  function handleFinish() {
    setStep('closed')
    onFinish(claimId)
  }

  const missingLabels = missingFields
    .map((field) => field.label)
    .join(MISSING_FIELDS_SEPARATOR)

  const content: Record<
    Exclude<ModalStep, 'closed'>,
    {
      variant: ActionModalVariant
      title: string
      description: string
    }
  > = {
    confirm: {
      variant: 'info',
      title: t('claims.submit.confirm.title'),
      description: t('claims.submit.confirm.description'),
    },
    incomplete: {
      variant: 'warning',
      title: t('claims.submit.incomplete.title'),
      description: t('claims.submit.incomplete.description', {
        fields: missingLabels,
      }),
    },
    submitted: {
      variant: 'success',
      title: t('claims.submit.submitted.title'),
      description: t('claims.submit.submitted.description'),
    },
  }

  const isConfirm = step === 'confirm'
  const isSubmitting = submitMutation.isPending

  // Props for ActionModal: confirm offers submit/cancel, the rest just close
  const primaryAction: ActionModalAction = isConfirm
    ? {
        label: t('claims.submit.confirm.submit'),
        onClick: handleSubmit,
        loading: isSubmitting,
        disabled: isSubmitting,
      }
    : { label: t('claims.submit.done'), onClick: handleFinish }
  const secondaryAction: ActionModalAction | undefined = isConfirm
    ? {
        label: t('claims.submit.confirm.cancel'),
        onClick: handleCancel,
        disabled: isSubmitting,
      }
    : undefined

  return {
    open,
    isOpen: step !== 'closed',
    ...(step === 'closed' ? content.confirm : content[step]),
    primaryAction,
    secondaryAction,
    onClose: isConfirm ? handleCancel : handleFinish,
    errorMessage: errorKey ? t(errorKey) : undefined,
  }
}
